import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Search, Lightbulb, Code2, Rocket, CheckCircle, ArrowRight, Users, ShoppingCart, Home, Car, MapPin, Building2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../sections/Navbar';
import Footer from '../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    icon: Search,
    number: '01',
    title: 'AI Visibility Audit',
    description: 'Provedeme hlubokou analýzu vašeho webu. Zjistíme, jak jste viditelní v ChatGPT, Perplexity a dalších AI nástrojích. Dostanete konkrétní report s prioritami.',
    color: '#0ea5e9',
  },
  {
    icon: Lightbulb,
    number: '02',
    title: 'Návrh řešení',
    description: 'Navrhneme konkrétní úpravy obsahu a struktury. Každé doporučení bude mít jasný cíl a měřitelný výstup.',
    color: '#00d4aa',
  },
  {
    icon: Code2,
    number: '03',
    title: 'Implementace',
    description: 'Upravíme obsah přímo na vašem webu. Zaměříme se na stránky s největším potenciálem. Pracujeme rychle a efektivně.',
    color: '#14b8a6',
  },
  {
    icon: Rocket,
    number: '04',
    title: 'Monitoring a růst',
    description: 'Sledujeme výsledky a pravidelně reportujeme. Upravujeme strategii podle změn v AI nástrojích. Rosteme společně.',
    color: '#22c55e',
  },
];

const forWho = [
  { icon: ShoppingCart, title: 'E-shopy', desc: 'Prodej produktů online' },
  { icon: Home, title: 'Reality', desc: 'Realitní kanceláře a portály' },
  { icon: Car, title: 'Auta / Bazar', desc: 'Prodej vozidel' },
  { icon: MapPin, title: 'Lokální služby', desc: 'Řemeslníci, restaurace' },
  { icon: Building2, title: 'B2B leadgen', desc: 'Firemní služby, SaaS' },
  { icon: FileText, title: 'Obsahové weby', desc: 'Blogy, magazíny' },
];

const benefits = [
  'Vyšší viditelnost v AI vyhledávačích',
  'Více kvalitních návštěv z organického vyhledávání',
  'Lepší konverzní poměr — návštěvníci jsou připravení',
  'Měřitelné výsledky a pravidelný reporting',
  'Předstih před konkurencí',
  'Dlouhodobý udržitelný růst',
];

const CooperationModelPage = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current?.querySelectorAll('.animate-section') || [], {
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <main className="relative z-10 pt-24 pb-16">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/">
              <Button
                variant="ghost"
                className="mb-6 text-slate-500 hover:text-slate-700 -ml-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět na hlavní stránku
              </Button>
            </Link>

            <div ref={contentRef}>
              {/* Header */}
              <div className="animate-section text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-4">
                  <Users className="w-4 h-4 text-[#0ea5e9]" />
                  <span className="text-sm text-slate-600">Jak to funguje</span>
                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-slate-800">
                  Model <span className="text-gradient">spolupráce</span>
                </h1>
                
                <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                  Od auditu přes optimalizaci až po pravidelný monitoring. 
                  Transparentní proces, který přináší měřitelné výsledky.
                </p>
              </div>

              {/* Process Steps */}
              <div className="animate-section mb-16">
                <h2 className="text-2xl font-bold mb-8 text-slate-800 text-center">4 kroky k úspěchu</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {steps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div
                        key={index}
                        className="glass-card rounded-xl p-6 relative group hover:scale-[1.02] transition-transform"
                      >
                        <div
                          className="absolute top-0 left-0 right-0 h-1 rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: step.color }}
                        />

                        <div className="flex items-center gap-4 mb-4">
                          <div
                            className="w-12 h-12 rounded-lg flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${step.color}30, ${step.color}10)`,
                              border: `1px solid ${step.color}40`,
                            }}
                          >
                            <Icon className="w-6 h-6" style={{ color: step.color }} />
                          </div>
                          <div>
                            <span
                              className="text-sm font-bold opacity-50"
                              style={{ color: step.color }}
                            >
                              Krok {step.number}
                            </span>
                            <h3 className="text-lg font-bold text-slate-800">{step.title}</h3>
                          </div>
                        </div>

                        <p className="text-slate-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* For Who */}
              <div className="animate-section mb-16">
                <h2 className="text-2xl font-bold mb-8 text-slate-800 text-center">Pro koho to dává smysl</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {forWho.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={index}
                        className="glass-card rounded-xl p-5 text-center hover:scale-[1.02] transition-transform"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20 flex items-center justify-center mx-auto mb-3">
                          <Icon className="w-5 h-5 text-[#0ea5e9]" />
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-1">{item.title}</h3>
                        <p className="text-sm text-slate-500">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Benefits */}
              <div className="animate-section mb-16">
                <h2 className="text-2xl font-bold mb-8 text-slate-800 text-center">Co získáte</h2>
                <div className="glass-card rounded-2xl p-6 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        </div>
                        <span className="text-slate-600">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="animate-section text-center">
                <div className="glass-card rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-4 text-slate-800">Připraveni začít?</h2>
                  <p className="text-slate-500 mb-6 max-w-lg mx-auto">
                    Začněte bezplatným AI Visibility Auditem. Zjistíme, jak je váš web připraven a co konkrétně optimalizovat.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/audit">
                      <Button
                        size="lg"
                        className="bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold px-8"
                      >
                        AI Visibility Audit
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/o-nas">
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-slate-300 text-slate-700 hover:bg-slate-100 px-8"
                      >
                        Kontaktujte nás
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CooperationModelPage;
